import {CSSObject} from '../types/css';
import {GetPaymentMethodsResponse} from './types';

export const transformStyle = (style: CSSObject) => {
  return Object.keys(style).reduce<any>((result, prop) => {
    if (typeof style[prop] === 'string') {
      result[prop] = style[prop];
    } else {
      result[prop] = `${style[prop]}px`;
    }
    return result;
  }, {});
};

export const loadScript = (
  src: string,
  nonce?: string,
  dataset?: Record<string, string | undefined>,
  forceReload?: boolean
): Promise<HTMLScriptElement> => {
  return new Promise((resolve, reject) => {
    const existingScript = Array.from(document.scripts).find((s) => s.src === src);
    if (existingScript) {
      if (forceReload) {
        existingScript.remove();
      } else {
        resolve(existingScript);
        return;
      }
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(script);
    script.onerror = (error) => reject(error);
    if (nonce) {
      script.setAttribute('nonce', nonce);
    }
    if (dataset) {
      Object.keys(dataset).forEach((key) => {
        if (dataset[key]) {
          script.dataset[key] = dataset[key];
        }
      });
    }
    document.head.appendChild(script);
  });
};

export const stringify = (obj: Record<string, any>) => {
  return Object.keys(obj)
    .reduce<string[]>((result, key) => {
      if (obj[key] !== undefined) {
        result.push(`${key}=${obj[key]}`);
      }
      return result;
    }, [])
    .join('&');
};

export const fixLocalstorage = () => {
  let localStorage: Storage;
  try {
    localStorage = window.localStorage;
  } catch (error) {
    const localStorageMock = (() => {
      let store = new Map();
      return {
        getItem(key: string): string {
          return store.get(key);
        },

        setItem: function (key: string, value: string) {
          store.set(key, value);
        },

        clear: function () {
          store = new Map();
        },

        removeItem: function (key: string) {
          store.delete(key);
        }
      };
    })();

    Object.defineProperty(window, 'localStorage', {value: localStorageMock});
    localStorage = localStorageMock as Storage;
  }
  return localStorage;
};

const getLanguage = () => {
  let lang;
  const localStorage = fixLocalstorage();
  try {
    lang = localStorage.getItem('i18nextLng');
  } catch (error) {}
  if (lang) {
    return lang.split('-')[0];
  }
  return '';
};

export const getClientEnv = () => {
  const env = [
    window.screen.colorDepth,
    window.screen.height,
    window.screen.width,
    new Date().getTimezoneOffset(),
    getLanguage()
  ].join('|');
  return window.btoa(env);
};

export const withError = async <T>(p: Promise<T>): Promise<[Error | undefined, T | undefined]> => {
  try {
    return [undefined, await p];
  } catch (err) {
    return [err, undefined];
  }
};

export const isMethodSupported = (config: GetPaymentMethodsResponse, paymentMethod: string) => {
  return config.paymentMethods && config.paymentMethods.includes(paymentMethod);
};

export const validateComponentProps = (props: any) => {
  if (props.paymentId) return;
  if (props.accountId && props.amount && props.currency) return;
  throw new Error('You need to provide paymentId or accountId amount and currency');
};
